import type { Ref } from "preact";
import { Gold } from "../components/Gold";
import { useThemeWeekUI, useAuctionUI, useBountyUI, useCheersUI, useClaimUI, useCompareUI, useEconomyUI, useGiveCreditUI, useLateRushUI, useNudgeUI, useProjectsUI, useQuickUI, useRouletteUI, useShareCreditUI, useStreakUI } from "../features";
import { SHARE_WORDS, creditedNames, giveBackLine, goldEach, helperChoices, sameShareName, shareQuestion, toggleShare, unshareQuestion } from "../../shared/shares.ts";
import { PROGRESS_PICK, PROJECT_WORDS, asBountyTask, canRemoveProgress, isProject, progressAddedLine, progressPosters } from "../../shared/projects.ts";
import { taskRouletteLine } from "../../shared/roulette.ts";
import { CLAIM_BUTTON_LABELS, claimButton, claimLabel, NUDGE_LIMIT_MESSAGE } from "../../shared/claims.ts";
import { boostForTask, flashForFlags } from "../../shared/flash.ts";
import { auctionPriceNow, isLuxuryTier } from "../../shared/economy.ts";
import { useMinuteTick } from "../useMinuteTick";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { api, ApiError } from "../api";
import { playAcknowledgement, playPeonLine, playWorkComplete } from "../audio";
import { setLastAward, setLastNotice } from "../award";
import { announceFind } from "../trinket-found";
import { displayCreator } from "../format";
import { navigate } from "../router";
import { jobFinished, playMoment } from "../tour-moments";
import type { FlashState, Role, Task } from "../../shared/types";
import { canDelete, canGiveCredit } from "../../shared/permissions.ts";
import { CREDIT_WORDS, creditNames, creditQuestion } from "../../shared/credit.ts";
import { CHEER_WORDS, addCheer, canCheer, cheerButtonName, cheerCrowd, cheersLine, hasCheered } from "../../shared/cheers.ts";
import { CheerIcon } from "../components/CheerIcon";
import { Confetti } from "../components/Confetti";
import { PeonStamp } from "../components/PeonStamp";
import { STREAK_WORD, bountyFor, reminderMinutesFor, reminderRule } from "../../shared/bounty.ts";
import { deviceZone, themeFor, themeMatches } from "../../shared/house-week.ts";
import { weekKey } from "../../shared/weekly.ts";
import { useLive } from "../live";
import { recallTask } from "../board-cache";
import { Lightbox } from "../components/Lightbox";
import { CompareLightbox } from "../components/CompareLightbox";
import { WhenButton } from "../components/WhenButton";
import { META_DOT } from "../../shared/when.ts";
import { COMPARE_WORDS } from "../../shared/compare.ts";
import { PhotoPicker } from "../components/PhotoPicker";
import { ErrorBox, ScreenTitle, Spinner } from "../components/Shell";
import { MentionText } from "../components/MentionText";
import { BusyLabel } from "../components/BusyLabel";
import { boardAdded, boardChanged, boardFinished, boardRemoved } from "./board-patch";
import { useEscape } from "../escape";

/**
 * A framed photo that appears instantly. The list has already fetched the
 * thumbnail, so it is drawn first and sets the frame's size; the full image
 * loads on top and fades in, so there is no empty frame and no layout jump. Also used by the
 * upgrade screen (src/views/UpgradeDetail.tsx).
 */
export function Photo({ id, caption, luxury, showCaption = false }: { id: string | null; caption: string; luxury?: boolean; showCaption?: boolean }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fullRef = useRef<HTMLImageElement>(null);
  // Reveal once the pixels are decoded, so the fade's first frame never stalls on a main-thread
  // decode. decode() can hang when the browser decides an invisible image needs no decoding
  // (Chrome, background tabs), so it is raced against a short timeout: a possible hitch beats
  // a photo that never appears.
  const reveal = (img: HTMLImageElement) => {
    const decoded = typeof img.decode === "function" ? img.decode().catch(() => undefined) : Promise.resolve();
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 300));
    void Promise.race([decoded, timeout]).then(() => {
      if (fullRef.current === img) setLoaded(true);
    });
  };
  // A cached full image can finish before the load handler is attached.
  useEffect(() => {
    setLoaded(false);
    const img = fullRef.current;
    if (img && img.complete && img.naturalWidth > 0) reveal(img);
  }, [id]);
  return (
    <figure class={`photo-figure${luxury ? " luxury" : ""}`}>
      {id ? (
        <div class="photo-stack" role="button" tabIndex={0} aria-label={`View ${caption.toLowerCase()} photo`} onClick={() => setOpen(true)} onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}>
          <img class="photo-base" src={api.photos.thumbUrl(id)} alt="" aria-hidden="true" />
          <img ref={fullRef} class={`photo-full${loaded ? " is-loaded" : ""}`} src={api.photos.url(id)} alt={caption} decoding="async" onLoad={(e) => reveal(e.currentTarget)} />
        </div>
      ) : (
        <div class="photo-none">No photo</div>
      )}
      {showCaption && <figcaption>{caption}</figcaption>}
      {open && id && <Lightbox src={api.photos.url(id)} alt={caption} onClose={() => setOpen(false)} />}
    </figure>
  );
}

/**
 * The names under a quiet word (Nudge, Give credit), opened inline: the focus goes to the first
 * name once they show, Escape closes them, and closing (Escape, the word, a pick) gives the focus back
 * to the word. One hook and one row (NameRow) for both, so the two stay identical.
 */
function useNameRow(names: string[] | null) {
  const [choosing, setChoosing] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const namesRef = useRef<HTMLDivElement>(null);
  const focusedNames = useRef(false);
  useEffect(() => {
    if (!choosing) {
      if (focusedNames.current) {
        focusedNames.current = false;
        const active = document.activeElement;
        // Only when the focus was in the panel (or fell to the page as it closed), never stolen from elsewhere.
        if (!active || active === document.body || active === openerRef.current) openerRef.current?.focus({ preventScroll: true });
      }
      return;
    }
    if (focusedNames.current) return;
    const first = namesRef.current?.querySelector<HTMLButtonElement>("button");
    if (first) {
      focusedNames.current = true;
      first.focus();
    }
  }, [choosing, names]);
  useEscape(choosing, () => {
    setChoosing(false);
    if (!focusedNames.current) openerRef.current?.focus({ preventScroll: true });
  });
  return { choosing, setChoosing, openerRef, namesRef };
}

/**
 * The row of names a quiet word opens: a group named for what it is for, one ghost button per name
 * (at least 44px tall, `.task-share-names .btn`), the picked one in its busy word while its request
 * is out and every name resting meanwhile.
 */
function NameRow({
  label,
  names,
  rowRef,
  pending,
  busyLabel,
  onPick,
  pressed,
}: {
  label: string;
  names: string[] | null;
  rowRef: Ref<HTMLDivElement>;
  pending: string | null;
  busyLabel?: string;
  onPick: (name: string) => void;
  /** Share credit (docs/shares.md): names already in, drawn pressed with the pill's check; a press takes them off. */
  pressed?: (name: string) => boolean;
}) {
  return (
    <div class="task-share-row task-share-names" role="group" aria-label={label} ref={rowRef}>
      {names === null && <span class="muted small">Finding peons…</span>}
      {names?.map((n) => {
        const on = pressed?.(n);
        return (
          <button key={n} type="button" class="btn btn-ghost" aria-pressed={pressed ? on : undefined} onClick={() => onPick(n)} disabled={pending !== null}>
            {pressed && <span class="pill-check" aria-hidden="true" />}
            <BusyLabel busy={pending === n} label={busyLabel}>{n}</BusyLabel>
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Nudge" (nudgeUI): opens the household's names and sends the one picked a single push,
 * "Nudge: <title>", once a day per task from this peon. Hidden in a household of one, where there is
 * nobody to nudge; it reads "Nudged" and rests until tomorrow once used.
 */
function NudgeTask({ id, peonName }: { id: string; peonName: string }) {
  /** Already nudged about this task today: the button reads "Nudged" and rests until tomorrow. */
  const [nudged, setNudged] = useState(false);
  useEffect(() => {
    let live = true;
    api.tasks.nudged(id, peonName).then(
      (r) => {
        if (live) setNudged(r.nudged_today);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [id, peonName]);
  const [names, setNames] = useState<string[] | null>(null);
  /** The nudge's word under the row; `error` lines are announced at once (role alert). */
  const [status, setStatusLine] = useState<{ text: string; error?: boolean } | null>(null);
  const setStatus = (text: string | null, error = false) => setStatusLine(text === null ? null : { text, error });
  /** A nudge is in flight (the ref locks out a double tap before the re-render), and to whom. */
  const sendingRef = useRef(false);
  const [sending, setSending] = useState<string | null>(null);
  // Read on arrival, so "Nudge" can stay hidden in a household of one.
  useEffect(() => {
    if (names) return;
    api.peons.list().then(
      ({ names: all }) => setNames(all.filter((n) => n.toLowerCase() !== peonName.toLowerCase())),
      () => setNames([]),
    );
  }, [names, peonName]);
  // The names open inline under the row (useNameRow): the focus goes to the first peon once they
  // show, Escape closes them, and closing (Escape, the toggle, a nudge) gives the focus back to "Nudge".
  const { choosing, setChoosing, openerRef, namesRef } = useNameRow(names);
  async function nudge(to: string) {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(to);
    setStatus(null);
    try {
      void playAcknowledgement();
      const tally = await api.tasks.nudge(id, peonName, to);
      setNudged(true);
      setChoosing(false);
      setStatus(tally.sent > 0 ? `Nudged ${to}.` : `${to} has notifications off.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Could not nudge";
      if (message === NUDGE_LIMIT_MESSAGE) {
        setNudged(true);
        setChoosing(false);
      }
      setStatus(message, true);
    } finally {
      sendingRef.current = false;
      setSending(null);
    }
  }
  // Nobody else in the house: nobody to nudge. Shown while the names load.
  if (names !== null && names.length === 0) return null;
  return (
    <div class="task-share">
      <div class="task-share-row">
        <button
          type="button"
          class="btn-quiet"
          ref={openerRef}
          disabled={nudged}
          onClick={() => setChoosing((c) => !c)}
          aria-expanded={choosing}
        >
          {nudged ? "Nudged" : "Nudge"}
        </button>
      </div>
      {choosing && <NameRow label="Nudge" names={names} rowRef={namesRef} pending={sending} busyLabel="Nudging…" onPick={(n) => void nudge(n)} />}
      {status &&
        (status.error ? (
          <p class="muted small task-share-status" role="alert">{status.text}</p>
        ) : (
          <p class="muted small task-share-status" role="status">{status.text}</p>
        ))}
    </div>
  );
}

/**
 * "Give credit" (giveCreditUI, docs/credit.md; owner, 2026-10-09: "When a job is done, there should
 * be a quiet way to give the credit to another peon"): the quiet word at the done task's foot, above
 * Reopen, for whoever holds the credit and for stewards (the caller decides, shared/permissions.ts
 * canGiveCredit). A tap opens the household's names but the holder's, in Nudge's row; a name asks
 * "Give credit to Hue? The gold goes with it." the way Reopen asks; yes moves the completion and
 * its gold, as paid, and the screen and the Done board show the new name. Nothing is announced: no
 * push, no peon line, only the press tick and the form's acknowledgement. Hidden in a household of one.
 */
function GiveCredit({ task, disabled, onCredited }: { task: Task; disabled: boolean; onCredited: (t: Task) => void }) {
  /** Every household name, read on arrival so the word can stay hidden when nobody else is here. */
  const [everyone, setEveryone] = useState<string[] | null>(null);
  useEffect(() => {
    let live = true;
    api.peons.list().then(
      ({ names }) => {
        if (live) setEveryone(names);
      },
      () => {
        if (live) setEveryone([]);
      },
    );
    return () => {
      live = false;
    };
  }, []);
  // A peon sharing the job (docs/shares.md) holds credit for it already, so is not offered.
  const names = useMemo(
    () => (everyone === null ? null : creditNames(everyone, task.done_by).filter((n) => !(task.shared_with ?? []).some((x) => sameShareName(x, n)))),
    [everyone, task.done_by, task.shared_with],
  );
  /** A refusal's words under the row, announced at once (role alert). */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The move is out (the ref locks out a double tap before the re-render), and to whom. */
  const givingRef = useRef(false);
  const [giving, setGiving] = useState<string | null>(null);
  const { choosing, setChoosing, openerRef, namesRef } = useNameRow(names);
  async function give(to: string) {
    if (givingRef.current) return;
    if (!confirm(creditQuestion(to))) return;
    givingRef.current = true;
    setGiving(to);
    setRefusal(null);
    try {
      const t = await api.tasks.update(task.id, { action: "credit", to });
      setChoosing(false);
      void playAcknowledgement();
      onCredited(t);
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      givingRef.current = false;
      setGiving(null);
    }
  }
  // Nobody to give it to. Shown while the names load, as Nudge is.
  if (names !== null && names.length === 0) return null;
  return (
    <div class="task-share credit-give">
      <button type="button" class="btn-quiet quiet-plain" ref={openerRef} disabled={disabled} onClick={() => setChoosing((c) => !c)} aria-expanded={choosing}>
        {CREDIT_WORDS.button}
      </button>
      {choosing && <NameRow label={CREDIT_WORDS.heading} names={names} rowRef={namesRef} pending={giving} onPick={(n) => void give(n)} />}
      {refusal && <p class="muted small task-share-status" role="alert">{refusal}</p>}
    </div>
  );
}

/**
 * "Share credit" (shareCreditUI, docs/shares.md; owner, 2026-11-02: "Should be able to share task credits
 * on things"): the quiet word at the done task's foot, above Give credit, for whoever holds the credit and
 * for stewards. A tap opens the household's names but the finisher's, those sharing it already pressed; a
 * name asks first, then joins (or leaves) and the gold is split again, a quarter more each. The row stays
 * open so several can be added. Quiet like Give credit: the press tick and the acknowledgement only.
 */
function ShareCredit({ task, disabled, onShared }: { task: Task; disabled: boolean; onShared: (t: Task) => void }) {
  const [everyone, setEveryone] = useState<string[] | null>(null);
  useEffect(() => {
    let live = true;
    api.peons.list().then(
      ({ names }) => {
        if (live) setEveryone(names);
      },
      () => {
        if (live) setEveryone([]);
      },
    );
    return () => {
      live = false;
    };
  }, []);
  const names = useMemo(() => (everyone === null ? null : creditNames(everyone, task.done_by)), [everyone, task.done_by]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const sharingRef = useRef(false);
  const [sharing, setSharing] = useState<string | null>(null);
  const { choosing, setChoosing, openerRef, namesRef } = useNameRow(names);
  const shared = task.shared_with ?? [];
  const isIn = (n: string) => shared.some((x) => sameShareName(x, n));
  async function toggle(name: string) {
    if (sharingRef.current) return;
    if (!confirm(isIn(name) ? unshareQuestion(name) : shareQuestion(name))) return;
    sharingRef.current = true;
    setSharing(name);
    setRefusal(null);
    try {
      const t = await api.tasks.update(task.id, { action: "share", with: toggleShare(shared, name) });
      void playAcknowledgement();
      onShared(t);
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      sharingRef.current = false;
      setSharing(null);
    }
  }
  if (names !== null && names.length === 0) return null;
  return (
    <div class="task-share credit-give">
      <button type="button" class="btn-quiet quiet-plain" ref={openerRef} disabled={disabled} onClick={() => setChoosing((c) => !c)} aria-expanded={choosing}>
        {SHARE_WORDS.button}
      </button>
      {choosing && <NameRow label={SHARE_WORDS.heading} names={names} rowRef={namesRef} pending={sharing} pressed={isIn} onPick={(n) => void toggle(n)} />}
      {refusal && <p class="muted small task-share-status" role="alert">{refusal}</p>}
    </div>
  );
}

/**
 * Job's Done's "Who helped?" (shareCreditUI, docs/shares.md), on the after photo's "Looks good?" check: one
 * pill per household name but the viewer's, Urgent's and Big job's look, a press toggling it. A project's
 * progress posters are in already and cannot be taken off (the server adds them whatever is sent).
 */
function HelperPills({ choices, picked, onToggle }: { choices: { name: string; locked: boolean }[]; picked: string[]; onToggle: (name: string) => void }) {
  return (
    <div class="helper-pick">
      <span class="helper-pick-label" id="helper-pick-label">{SHARE_WORDS.heading}</span>
      <div class="task-flag-pills" role="group" aria-labelledby="helper-pick-label">
        {choices.map(({ name, locked }) => {
          const on = locked || picked.some((p) => sameShareName(p, name));
          return (
            <button key={name} type="button" class={`task-flag-pill task-flag-big${on ? " on" : ""}`} aria-pressed={on} disabled={locked} onClick={() => onToggle(name)}>
              <span class="pill-check" aria-hidden="true" />
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A project's progress photos (projectsUI, docs/projects.md): a small "Progress" label over the photos,
 * oldest first, each a square thumbnail wearing its poster's stamp; a tap shows it full screen. While the
 * project is open the poster (any, for a steward) has a quiet Remove under theirs, after a yes.
 */
function ProgressStrip({ task, peonName, role, onChanged }: { task: Task; peonName: string; role: Role; onChanged: (t: Task) => void }) {
  const [viewing, setViewing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const progress = task.progress ?? [];
  if (progress.length === 0) return null;
  const open = task.status === "open";
  async function remove(pid: string) {
    if (removing || !confirm(PROJECT_WORDS.removeQuestion)) return;
    setRemoving(pid);
    setRefusal(null);
    try {
      const t = await api.tasks.removeProgress(task.id, pid);
      void playAcknowledgement();
      onChanged(t);
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setRemoving(null);
    }
  }
  return (
    <div class="progress-strip">
      <span class="progress-label" id={`progress-${task.id}`}>{PROJECT_WORDS.progress}</span>
      <ul class="progress-photos" aria-labelledby={`progress-${task.id}`}>
        {progress.map((p) => (
          <li key={p.id} class="progress-item">
            <button type="button" class="progress-thumb" onClick={() => setViewing(p.photo_id)} aria-label={`Progress photo from ${p.peon_name}`}>
              <img src={api.photos.thumbUrl(p.photo_id)} alt="" loading="lazy" />
              <PeonStamp name={p.peon_name} label={p.peon_name} className="progress-stamp" />
            </button>
            {open && canRemoveProgress(p, peonName, role) && (
              <button type="button" class="btn-quiet quiet-plain progress-remove" disabled={removing !== null} onClick={() => void remove(p.id)}>
                <BusyLabel busy={removing === p.id}>{PROJECT_WORDS.remove}</BusyLabel>
              </button>
            )}
          </li>
        ))}
      </ul>
      {refusal && <p class="muted small task-share-status" role="alert">{refusal}</p>}
      {viewing && <Lightbox src={api.photos.url(viewing)} alt={PROJECT_WORDS.progress} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** Task ids this tab has already reported a `viewed` event for, so a re-render or a live refresh never resends it. */
const viewedTaskIds = new Set<string>();

export function TaskDetail({ id, peonName, role }: { id: string; peonName: string; role: Role }) {
  const bountyUIEnabled = useBountyUI();
  const economyUIEnabled = useEconomyUI();
  const quickUIEnabled = useQuickUI();
  // The late chore rush (docs/bounties.md "Late chore rush") rides in the gold rush's state.
  const lateRushEnabled = useLateRushUI();
  const claimUIEnabled = useClaimUI();
  /** Task roulette (docs/roulette.md): the muted line naming who the wheel picked, under the meta. */
  const rouletteUIEnabled = useRouletteUI();
  const nudgeUIEnabled = useNudgeUI();
  const compareUIEnabled = useCompareUI();
  const streakUIEnabled = useStreakUI();
  const auctionUIEnabled = useAuctionUI();
  /** Give credit (docs/credit.md): the quiet word at a done task's foot, above Reopen. */
  const giveCreditUIEnabled = useGiveCreditUI();
  /** Cheers (docs/cheers.md): the Cheer word at a done task's foot, and the line saying who cheered. */
  const cheersUIEnabled = useCheersUI();
  const themeWeekUIEnabled = useThemeWeekUI();
  /** Shared credit (docs/shares.md): Job's Done's "Who helped?" and Share credit at a done task's foot. */
  const shareCreditUIEnabled = useShareCreditUI();
  /** Projects (docs/projects.md): a big job's pot, its progress photos and Add progress. */
  const projectsUIEnabled = useProjectsUI();
  /**
   * The viewer's bounty streak, days in a row with today counted (streakUI, docs/bounties.md
   * "Streak"): passed to bountyFor so the gold shown is what finishing pays, and the meta line can
   * say "Streak" when it is the bonus in force. Null until known, or with the flag off.
   */
  const [streakDays, setStreakDays] = useState<number | null>(null);
  /** The before and after open in the compare viewer (compareUI). */
  const [comparing, setComparing] = useState(false);
  // Opens with the task as the board last showed it (src/board-cache.ts), so the photo, title and
  // meta line are there on the first frame; the fetch below replaces it with the fresh row.
  const [task, setTask] = useState<Task | null>(() => recallTask(id));
  const [flash, setFlash] = useState<FlashState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Which of the screen's own buttons is waiting on the server, so it alone reads "Working…". */
  const [pressed, setPressed] = useState<"reopen" | "delete" | null>(null);
  const now = useMinuteTick();

  const reload = useLive(
    async (initial) => {
      if (busy) return; // don't clobber an in-flight complete/delete
      try {
        const [t, flashResult] = await Promise.allSettled([
          api.tasks.get(id),
          quickUIEnabled || lateRushEnabled ? api.flash.state(peonName) : Promise.resolve(null),
        ]);
        if (t.status === "fulfilled") {
          setTask(t.value);
          if (initial) setError(null);
        } else if (initial && t.reason instanceof ApiError && t.reason.status === 404) {
          // The task has gone (deleted since a push or a link named it): the board, not an error
          // (docs/mentions.md "Where a tag lands").
          navigate({ name: "open" }, true);
          return;
        } else if (initial) {
          setError(t.reason instanceof Error ? t.reason.message : "Could not load");
        }
        if (flashResult.status === "fulfilled") setFlash(flashForFlags(flashResult.value, quickUIEnabled, lateRushEnabled));
      } catch (e) {
        if (initial) setError(e instanceof Error ? e.message : "Could not load");
      }
    },
    [id, peonName, quickUIEnabled, lateRushEnabled],
  );

  // The streak is only asked for with its flag on and an open task to pay it.
  const wantStreak = streakUIEnabled && task?.status === "open";
  useEffect(() => {
    if (!wantStreak) return setStreakDays(null);
    let live = true;
    api.streak
      .get()
      .then((s) => live && setStreakDays(s.days))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [wantStreak, id, peonName]);

  useEffect(() => {
    if (!task || task.status !== "open" || viewedTaskIds.has(id)) return;
    viewedTaskIds.add(id);
    api.tasks.event(id, "viewed", peonName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, task?.status]);

  // The peon is handed the job when an open task's screen appears, and grumbles ("Five more
  // minutes.") when it is left without being finished or deleted: the screen unmounts on back, on
  // the header link and on a browser gesture alike. Completing or deleting sets `settled` first, so
  // the completion or delete line is not chased by an abandon. Both are once per visit and keyed on
  // the task alone: a live update (someone else finishing it, or reopening it) plays neither, and
  // a task that is no longer open when this screen is left was not abandoned by this peon.
  const settled = useRef(false);
  /** The opened line played on this visit, so leaving may grumble. */
  const voiced = useRef(false);
  /** The first status of this visit has been looked at. */
  const decided = useRef(false);
  const statusRef = useRef(task?.status);
  statusRef.current = task?.status;
  // Declared before the effect below so that, on a change of id, the old visit ends first.
  useEffect(() => {
    return () => {
      if (voiced.current && !settled.current && statusRef.current === "open") void playPeonLine("abandon");
      voiced.current = false;
      decided.current = false;
      settled.current = false;
    };
  }, [id]);
  useEffect(() => {
    if (decided.current || !task || task.id !== id) return;
    decided.current = true;
    if (task.status !== "open") return;
    voiced.current = true;
    void playPeonLine("open");
  }, [id, task]);

  // The first open task this device opens explains itself (the "task-opened" moment): once per
  // visit, after the screen shows the task, so the cards point at its Job's Done and its gold.
  const toured = useRef<string | null>(null);
  useEffect(() => {
    if (!task || task.id !== id || task.status !== "open" || toured.current === id) return;
    toured.current = id;
    playMoment("task-opened");
  }, [id, task]);

  /** Job's Done's "Who helped?" (shareCreditUI): the household's names, read once the task is open, and the ones picked. */
  const [household, setHousehold] = useState<string[] | null>(null);
  const [helpers, setHelpers] = useState<string[]>([]);
  const wantHousehold = shareCreditUIEnabled && task?.status === "open";
  useEffect(() => {
    if (!wantHousehold || household) return;
    let live = true;
    api.peons.list().then(
      ({ names }) => live && setHousehold(names),
      () => live && setHousehold([]),
    );
    return () => {
      live = false;
    };
  }, [wantHousehold, household]);
  const project = !!task && isProject(task, projectsUIEnabled);
  const helperRow = household && task ? helperChoices(household, peonName, project ? progressPosters(task.progress) : []) : [];

  /** Add progress (projectsUI): the request is out, and what it said once in ("+20 gold in the pot."). */
  const [progressBusy, setProgressBusy] = useState(false);
  const [progressLine, setProgressLine] = useState<{ text: string; error?: boolean } | null>(null);
  useEffect(() => {
    if (!progressLine || progressLine.error) return;
    const t = setTimeout(() => setProgressLine(null), 4000);
    return () => clearTimeout(t);
  }, [progressLine]);
  async function addProgress(photoId: string) {
    if (progressBusy) return;
    setProgressBusy(true);
    setProgressLine(null);
    try {
      const t = await api.tasks.addProgress(id, peonName, photoId);
      setTask(t);
      boardChanged(t);
      void playAcknowledgement();
      const mine = [...(t.progress ?? [])].reverse().find((p) => sameShareName(p.peon_name, peonName) && p.photo_id === photoId);
      setProgressLine({ text: progressAddedLine(mine?.gold ?? 0) });
    } catch (e) {
      setProgressLine({ text: e instanceof Error ? e.message : "Something went wrong", error: true });
    } finally {
      setProgressBusy(false);
    }
  }

  /** The after photo the peon confirmed; kept when the completion fails so a retry sends it again. */
  const [donePhoto, setDonePhoto] = useState<string | null>(null);
  const [doneError, setDoneError] = useState<string | null>(null);
  const completing = useRef(false);

  async function complete(photoId: string, retry: boolean) {
    if (completing.current) return;
    completing.current = true;
    settled.current = true;
    setDonePhoto(photoId);
    setDoneError(null);
    setBusy(true);
    // The completion line on the first try; a retry is a plain press.
    if (retry) void playAcknowledgement();
    else void playWorkComplete();
    try {
      // Shared credit (docs/shares.md): the names picked on the photo check, the locked ones included.
      const withHelpers = shareCreditUIEnabled ? [...helperRow.filter((c) => c.locked).map((c) => c.name), ...helpers] : [];
      const t = await api.tasks.update(id, { action: "complete", done_photo_id: photoId, done_by: peonName, ...(withHelpers.length ? { helpers: withHelpers } : {}) });
      setTask(t);
      // The board lands with the card already moved to Done; its own fetch then confirms it.
      boardFinished(t);
      // The board it lands on says what became of the job, the first time (src/tour-moments.ts).
      jobFinished();
      // A shared job's toast is the finisher's own part (docs/shares.md).
      setLastAward(goldEach(t));
      // Trinkets (docs/trinkets.md): the chore turned one up; the find sheet comes up over the board.
      if (t.trinket_found) announceFind(t.trinket_found);
      // With no gold toast to show (bounties off, or nothing paid), the board still says where it went.
      if (!bountyUIEnabled || !(t.gold_awarded && t.gold_awarded > 0)) setLastNotice("Moved to Done");
      navigate({ name: "open" }, true);
    } catch (e) {
      // Not finished after all: leaving now is an abandon again.
      settled.current = false;
      setDoneError(e instanceof Error ? e.message : "Could not finish the job");
    } finally {
      completing.current = false;
      setBusy(false);
    }
  }

  async function run(which: "reopen" | "delete", fn: () => Promise<Task>, after?: (t: Task) => void) {
    setBusy(true);
    setPressed(which);
    setError(null);
    try {
      const t = await fn();
      setTask(t);
      after?.(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
      setPressed(null);
    }
  }

  /** "Me do it" or "Never mind" waiting on the server, and a refusal's words ("Hue is on it."). */
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  async function toggleClaim(kind: "claim" | "release") {
    if (claiming) return;
    setClaiming(true);
    setClaimError(null);
    void playAcknowledgement();
    try {
      const t = kind === "claim" ? await api.tasks.claim(id, peonName) : await api.tasks.release(id, peonName);
      setTask(t);
      boardChanged(t);
    } catch (e) {
      setClaimError(e instanceof Error ? e.message : "Something went wrong");
      // Someone else got there first: show their claim.
      void api.tasks.get(id).then(setTask, () => {});
    } finally {
      setClaiming(false);
    }
  }

  /**
   * Cheers (docs/cheers.md "The task screen"): a press answers at once (your stamp in the crowd, the
   * confetti, the chime), the request runs behind it, the server's cheers replace it, and a refusal
   * puts it back with the server's words. A cheer is for keeps; the button goes once the confetti has
   * fallen. `cheerBurst` is the burst's seed while it falls.
   */
  const [cheerError, setCheerError] = useState<string | null>(null);
  const [cheerBurst, setCheerBurst] = useState<number | null>(null);
  const cheering = useRef(false);
  async function cheer() {
    if (!task || cheering.current || hasCheered(task.cheers, peonName)) return;
    const before = task.cheers ?? [];
    cheering.current = true;
    setCheerError(null);
    setCheerBurst(Date.now());
    void playAcknowledgement();
    setTask((cur) => (cur && cur.id === task.id ? { ...cur, cheers: addCheer(cur.cheers, peonName) } : cur));
    try {
      const t = await api.tasks.cheer(task.id, peonName);
      setTask((cur) => (cur && cur.id === t.id ? t : cur));
      // The remembered Done board carries the cheers too, so this screen opens with them next time.
      boardChanged(t);
    } catch (e) {
      setTask((cur) => (cur && cur.id === task.id ? { ...cur, cheers: before } : cur));
      setCheerBurst(null);
      setCheerError(e instanceof Error ? e.message : "Could not cheer");
    } finally {
      cheering.current = false;
    }
  }

  // Delete shows only to who may delete (shared/permissions.ts): a steward, or whoever posted it.
  const mayDelete = !!task && canDelete({ name: peonName, role }, task.created_by);

  return (
    <>
      <main class="content content-wide task-detail">
        <ScreenTitle title={task?.title ?? "Task"} mentions />
        {error && <ErrorBox message={error} onRetry={task ? undefined : reload} />}
        {!task && !error && <Spinner />}
        {task && (
          <>
            {/* Two panes on a wide screen (src/styles.css "wide layout"): the photo, or the done pair, on the
                left and everything else on the right. Below 1024px both wrappers are display: contents, so
                the column is as it was. */}
            <div class="detail-media">
              {/* The photo is the proof and the point: first and largest. A done task shows the pair. */}
              {task.status === "done" ? (
                <div class="photo-pair">
                  <Photo id={task.start_photo_id} caption="Before" showCaption luxury={economyUIEnabled && isLuxuryTier(task.luxury)} />
                  <Photo id={task.done_photo_id} caption="After" showCaption luxury={economyUIEnabled && isLuxuryTier(task.luxury)} />
                </div>
              ) : (
                <Photo id={task.start_photo_id} caption="Before" luxury={economyUIEnabled && isLuxuryTier(task.luxury)} />
              )}
              {/* A project's progress photos (projectsUI, docs/projects.md), between the before and the after. */}
              {projectsUIEnabled && !!task.big_job && <ProgressStrip task={task} peonName={peonName} role={role} onChanged={(t) => { setTask(t); boardChanged(t); }} />}
              {/* The compare viewer (compareUI): both photos one over the other, a handle to wipe between them. */}
              {compareUIEnabled && task.status === "done" && task.start_photo_id && task.done_photo_id && (
                <div class="compare-row">
                  <button type="button" class="btn-quiet compare-open" onClick={() => setComparing(true)}>
                    {COMPARE_WORDS.button}
                  </button>
                  {comparing && (
                    <CompareLightbox before={api.photos.url(task.start_photo_id)} after={api.photos.url(task.done_photo_id)} onClose={() => setComparing(false)} />
                  )}
                </div>
              )}
            </div>
            <div class="detail-side">

              {(() => {
                // One line under the photo: who, when, and what it pays (bonuses already included).
                const boost = boostForTask(flash, task.id);
                const open = task.status === "open";
                // With lateRushUI on the reminder's double follows the viewer's own late chore rush window.
                const reminder = reminderRule(lateRushEnabled, flash?.late_rush);
                // Themed weeks (docs/house-week.md): a chore matching this week's theme pays extra.
                const theme = open && themeWeekUIEnabled ? themeFor(weekKey(new Date(now), deviceZone())) : null;
                const themed = themeMatches(theme, task.title);
                // A project (projectsUI, docs/projects.md) pays its pot, not an aged big job's gold.
                const bounty = open ? bountyFor(asBountyTask(task, projectsUIEnabled), now, boost, peonName, streakUIEnabled ? (streakDays ?? 0) : 0, false, reminder, themed) : null;
                // The luxury auction (auctionUI): a rising luxury task shows its current total, the
                // offer plus the house's part, and says so on a muted line under the meta.
                const rising = open && auctionUIEnabled ? auctionPriceNow(task, now) : null;
                // A shared job (docs/shares.md): everyone it is credited to, "Hue, Jeff and Bones", and
                // each one's part of the gold, said with "each".
                const credited = creditedNames(task);
                const sharedNames =
                  credited.length > 1
                    ? credited.slice(1).map((n, i) => (
                        <span key={n}>
                          {i === credited.length - 2 ? " and " : ", "}
                          <strong><MentionText text={n} /></strong>
                        </span>
                      ))
                    : null;
                const gold = bounty ? (rising ? rising.total : bounty.total) : goldEach(task);
                const showGold = bountyUIEnabled && gold > 0;
                const streakShown = showGold && streakUIEnabled && !!bounty?.streak;
                // The overdue reminder's window (shared/bounty.ts), or the late chore rush's: the gold above is already doubled.
                const reminderMin = task.status === "open" && showGold ? reminderMinutesFor(task, now, reminder) : null;
                return (
                  <>
                  <p class="detail-meta">
                    {/* One sentence that wraps like prose after its dots; the time is one unbreakable unit (shared/when.ts). */}
                    {task.status === "done" ? (
                      <>
                        Done by <strong><MentionText text={task.done_by ?? ""} /></strong>
                        {sharedNames}
                        {META_DOT}
                        <WhenButton key={`done-${task.done_at}`} iso={task.done_at} now={now} rest="exact" />
                      </>
                    ) : (
                      <>
                        <strong>{displayCreator(task)}</strong>
                        {META_DOT}
                        <WhenButton key={`open-${task.created_at}`} iso={task.created_at} now={now} rest="ago" />
                      </>
                    )}
                    {showGold && (
                      <>
                        {/* A done task's gold is its own line under who and when, no dot after the time
                            (owner, 2026-10-05: "The dot after the time here doesn't make sense if the gold
                            is on the next line"). The open task's gold still follows its dot. */}
                        {task.status === "done" ? <br /> : META_DOT}
                        <span class="detail-gold" data-tour="coins">
                          <Gold amount={gold} />
                        </span>
                        {task.status === "done" && credited.length > 1 && <span class="detail-each"> {SHARE_WORDS.each}</span>}
                      </>
                    )}
                    {streakShown && (
                      <>
                        {META_DOT}
                        <span class="detail-streak">{STREAK_WORD}</span>
                      </>
                    )}
                    {showGold && themed && theme && (
                      <>
                        {META_DOT}
                        <span class="detail-streak">{theme.name}</span>
                      </>
                    )}
                    {task.status === "open" && !!task.urgent && (
                      <>
                        {META_DOT}
                        <span class="detail-urgent">Urgent</span>
                      </>
                    )}
                    {open && project && (
                      <>
                        {META_DOT}
                        <span class="detail-streak">{PROJECT_WORDS.meta}</span>
                      </>
                    )}
                  </p>
                  {reminderMin !== null && <p class="detail-reminder">Double gold for {reminderMin} more min.</p>}
                  {claimUIEnabled && (() => {
                    const line = claimLabel(task, peonName, now);
                    return line ? <p class="detail-claim">{line}</p> : null;
                  })()}
                  {rouletteUIEnabled && task.status === "open" && task.roulette_winner && task.roulette_stake ? (
                    <p class="detail-claim detail-roulette">{taskRouletteLine(task.roulette_winner, peonName, task.roulette_stake)}</p>
                  ) : null}
                  {/* Cheers (docs/cheers.md "The task screen"): who cheered the done job, as the row of their
                      peon stamps under "Cheered by", the doer included; nothing at zero. */}
                  {cheersUIEnabled && task.status === "done" && (() => {
                    const crowd = cheerCrowd(task.cheers, task.done_by);
                    return crowd.length > 0 ? (
                      <div class="detail-cheers" role="img" aria-label={cheersLine(task.cheers, task.done_by, peonName)}>
                        <span class="detail-cheers-label" aria-hidden="true">{CHEER_WORDS.crowd}</span>
                        <span class="cheer-crowd">
                          {crowd.map((n, i) => (
                            <PeonStamp key={n} name={n} label={n} className={cheerBurst !== null && i === crowd.length - 1 ? "cheer-stamp pop" : "cheer-stamp"} />
                          ))}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  </>
                );
              })()}
              {/* The description is prose, not a box (owner, 2026-10-05: "This screen is super busy"). */}
              {task.notes && (
                <p class="detail-notes">
                  <MentionText text={task.notes} />
                </p>
              )}
              {task.status === "open" && (
                <div class="actions">
                  <PhotoPicker
                    variant="button"
                    label="Job's Done"
                    tour="job-done"
                    confirmLabel="Job's Done"
                    photoId={null}
                    onChange={() => {}}
                    onStart={() => api.tasks.event(id, "done_started", peonName)}
                    onCancel={() => api.tasks.event(id, "done_abandoned", peonName)}
                    onConfirm={(photoId) => void complete(photoId, false)}
                    working={busy}
                    onPress={donePhoto ? () => void complete(donePhoto, true) : undefined}
                    checkExtras={
                      shareCreditUIEnabled && helperRow.length > 0 ? (
                        <HelperPills choices={helperRow} picked={helpers} onToggle={(n) => setHelpers((h) => toggleShare(h, n))} />
                      ) : undefined
                    }
                  />
                  {doneError && <ErrorBox message={doneError} />}
                  {/* Add progress (projectsUI, docs/projects.md): a photo of the work so far grows the pot. */}
                  {project && (
                    <PhotoPicker
                      variant="button"
                      tone="ghost"
                      label={PROJECT_WORDS.add}
                      restoreKey={PROGRESS_PICK}
                      confirmLabel={PROJECT_WORDS.confirm}
                      photoId={null}
                      onChange={() => {}}
                      onConfirm={(photoId) => void addProgress(photoId)}
                      working={progressBusy || busy}
                    />
                  )}
                  {progressLine &&
                    (progressLine.error ? (
                      <p class="muted small task-share-status" role="alert">{progressLine.text}</p>
                    ) : (
                      <p class="detail-reminder progress-added" role="status">{progressLine.text}</p>
                    ))}
                  {claimUIEnabled && (() => {
                    // "Me do it" when nobody holds it, "Never mind" on your own; nothing while someone else does.
                    const kind = claimButton(task, peonName, now);
                    return kind ? (
                      <button type="button" class="btn btn-ghost" disabled={busy || claiming} onClick={() => void toggleClaim(kind)}>
                        <BusyLabel busy={claiming}>{CLAIM_BUTTON_LABELS[kind]}</BusyLabel>
                      </button>
                    ) : null;
                  })()}
                  {claimError && <p class="muted small task-share-status" role="alert">{claimError}</p>}
                </div>
              )}
              {task.status === "open" && nudgeUIEnabled && <NudgeTask id={id} peonName={peonName} />}
              {task.status === "open" && mayDelete && (
                <div class="danger-zone">
                  <button
                    type="button"
                    class="btn-quiet danger"
                    disabled={busy}
                    onClick={() => {
                      if (!confirm("Delete this task? This cannot be undone.")) return;
                      settled.current = true;
                      void run("delete", () => api.tasks.remove(id), () => {
                        boardRemoved(id);
                        void playPeonLine("delete");
                        navigate({ name: "open" }, true);
                      });
                    }}
                  >
                    <BusyLabel busy={pressed === "delete"}>Delete task</BusyLabel>
                  </button>
                </div>
              )}

              {/* The done task's foot: a small column of quiet actions, Reopen above Delete record (owner,
                  2026-10-05: "This screen is super busy"), and with giveCreditUI "Give credit" above
                  Reopen for whoever holds the credit and for stewards (docs/credit.md). */}
              {/* Cheers (docs/cheers.md "The task screen"): the gold Cheer! for anyone but the peon it is
                  credited to who has not cheered it, for keeps (owner, 2026-10-28: "it should not be a
                  toggle at all"); the confetti falls over the screen and their stamp joins the crowd. */}
              {cheersUIEnabled && canCheer(task, peonName) && (!hasCheered(task.cheers, peonName) || cheerBurst !== null) && (
                <div class="actions cheer-give">
                  <button type="button" class="btn btn-primary btn-block cheer-go" disabled={busy || cheerBurst !== null} aria-label={cheerButtonName(task.done_by)} onClick={() => void cheer()}>
                    <CheerIcon key={cheerBurst ?? 0} popped={cheerBurst !== null} scale={3} className={cheerBurst !== null ? "cheer-icon pop" : "cheer-icon"} />
                    <span>{cheerBurst !== null ? CHEER_WORDS.sent : CHEER_WORDS.button}</span>
                  </button>
                  {cheerError && <p class="muted small task-share-status" role="alert">{cheerError}</p>}
                </div>
              )}
              {cheerBurst !== null && <Confetti seed={cheerBurst} onDone={() => setCheerBurst(null)} />}
              {task.status === "done" && (
                <div class="danger-zone quiet-foot">
                  {shareCreditUIEnabled && canGiveCredit(task, peonName, role) && (
                    <ShareCredit
                      task={task}
                      disabled={busy}
                      onShared={(t) => {
                        setTask(t);
                        boardChanged(t);
                      }}
                    />
                  )}
                  {giveCreditUIEnabled && canGiveCredit(task, peonName, role) && (
                    <GiveCredit
                      task={task}
                      disabled={busy}
                      onCredited={(t) => {
                        setTask(t);
                        // The Done board lands with the new name on the card; its own fetch then confirms it.
                        boardChanged(t);
                      }}
                    />
                  )}
                  <button
                    type="button"
                    class="btn-quiet quiet-plain"
                    disabled={busy}
                    onClick={() => {
                      // The finisher's gold goes back with the reopen (bounties on and something was paid).
                      const paid = bountyUIEnabled && (task.gold_awarded ?? 0) > 0 && task.done_by;
                      if (!confirm(paid ? `Reopen it? ${giveBackLine(creditedNames(task))}` : "Reopen this task? It goes back to the board without its after photo.")) return;
                      void playAcknowledgement();
                      void run("reopen", () => api.tasks.update(id, { action: "reopen" }), (t) => {
                        boardAdded(t);
                        // Reopened here: no opened line on top of the reopen line, but leaving it now is an abandon.
                        decided.current = true;
                        voiced.current = true;
                        void playPeonLine("reopen");
                      });
                    }}
                  >
                    <BusyLabel busy={pressed === "reopen"}>Reopen</BusyLabel>
                  </button>
                  {mayDelete && (
                    <button
                      type="button"
                      class="btn-quiet danger"
                      disabled={busy}
                      onClick={() => {
                        if (!confirm("Delete this record? This cannot be undone.")) return;
                        void run("delete", () => api.tasks.remove(id), () => {
                          boardRemoved(id);
                          void playPeonLine("delete");
                          navigate({ name: "done" }, true);
                        });
                      }}
                    >
                      <BusyLabel busy={pressed === "delete"}>Delete record</BusyLabel>
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}
